import {
  Container,
  Grid,
  Box,
  Fade,
  Grow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useState, useEffect, useRef } from "react";
import Axios from "axios";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

import { Loading } from "../../components/shared/loading";
import { TypingQuote } from "../../components/shared/TypingQuote";
import { HeroParticles } from "../../components/shared/HeroParticles";
import { resolveIcon } from "../../data/productIcons";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "product", label: "Products" },
  { key: "saas", label: "SaaS" },
  { key: "game", label: "Games" },
];
// Category group for a product (falls back sensibly for older data).
const groupOf = (p) =>
  p.group ||
  (p.status === "saas" ? "saas" : p.status === "soon" ? "game" : "product");

const useStyles = makeStyles((theme) => ({
  "@keyframes heroFadeUp": {
    from: { opacity: 0, transform: "translateY(24px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes heroExpand": {
    from: { width: 0, opacity: 0 },
    to: { width: 90, opacity: 1 },
  },
  "@keyframes heroShimmerSweep": {
    "0%": { backgroundPosition: "-200% center" },
    "100%": { backgroundPosition: "200% center" },
  },
  "@keyframes orbDrift": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(14px, -12px) scale(1.12)" },
  },
  "@keyframes orbDriftAlt": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(-16px, 10px) scale(1.08)" },
  },
  "@keyframes dotPulse": {
    "0%, 100%": { boxShadow: "0 0 0 0 rgba(124,252,155,0.6)" },
    "50%": { boxShadow: "0 0 0 5px rgba(124,252,155,0)" },
  },
  "@keyframes dotPulseSoon": {
    "0%, 100%": { boxShadow: "0 0 0 0 rgba(242,193,78,0.6)" },
    "50%": { boxShadow: "0 0 0 5px rgba(242,193,78,0)" },
  },

  // ── Hero ──
  heroSection: {
    position: "relative",
    overflow: "hidden",
    minHeight: "52vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(135deg, #1a001a 0%, #2d002d 50%, #1a001a 100%)",
  },
  heroOrb: {
    position: "absolute",
    borderRadius: "50%",
    filter: "blur(60px)",
    pointerEvents: "none",
    zIndex: 0,
  },
  heroOrbOne: {
    width: 340,
    height: 340,
    top: "-16%",
    right: "6%",
    background:
      "radial-gradient(circle, rgba(199,125,199,0.35), transparent 68%)",
    animation: "$orbDrift 9s ease-in-out infinite",
  },
  heroOrbTwo: {
    width: 300,
    height: 300,
    bottom: "-22%",
    left: "2%",
    background:
      "radial-gradient(circle, rgba(155,59,155,0.3), transparent 68%)",
    animation: "$orbDriftAlt 11s ease-in-out infinite",
  },
  heroContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    color: "#ffffff",
    padding: "56px 20px 72px",
    maxWidth: 760,
  },
  heroEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "2.4px",
    textTransform: "uppercase",
    color: "#e0b8e0",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(199,125,199,0.35)",
    borderRadius: 30,
    padding: "6px 16px",
    marginBottom: 18,
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) both",
    "&::before": {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: "#c77dc7",
      boxShadow: "0 0 10px #c77dc7",
    },
  },
  heroTitle: {
    fontSize: "clamp(1.7rem, 4vw, 2.6rem)",
    fontWeight: 700,
    letterSpacing: "-0.02em",
    marginBottom: 10,
    backgroundImage:
      "linear-gradient(90deg, #ffffff 0%, #e0b8e0 25%, #c77dc7 50%, #e0b8e0 75%, #ffffff 100%)",
    backgroundSize: "200% auto",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
    animation:
      "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both, $heroShimmerSweep 5s linear infinite",
  },
  heroSubtitle: {
    fontSize: "clamp(0.82rem, 1.4vw, 0.94rem)",
    fontWeight: 400,
    opacity: 0.88,
    maxWidth: 520,
    margin: "0 auto",
    lineHeight: 1.65,
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.35s both",
  },
  heroStats: {
    display: "flex",
    justifyContent: "center",
    alignItems: "stretch",
    gap: "0px",
    marginTop: 30,
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both",
    [theme.breakpoints.down("xs")]: { flexWrap: "wrap", gap: 14 },
  },
  heroStat: {
    padding: "0 26px",
    textAlign: "center",
    "& + &": {
      borderLeft: "1px solid rgba(255,255,255,0.14)",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "0 12px",
      "& + &": { borderLeft: "none" },
    },
  },
  heroStatValue: {
    fontSize: "1.35rem",
    fontWeight: 700,
    color: "#ffffff",
    lineHeight: 1.1,
    "& span": { color: "#c77dc7" },
  },
  heroStatLabel: {
    fontSize: "0.64rem",
    fontWeight: 600,
    letterSpacing: "1.6px",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)",
    marginTop: 5,
  },

  // ── Section ──
  section: {
    padding: "0 0 44px",
    background: "#fafafa",
  },
  // Floating glass filter bar, overlapping the hero's bottom edge.
  filterBar: {
    position: "relative",
    zIndex: 3,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "8px",
    width: "fit-content",
    maxWidth: "calc(100% - 32px)",
    margin: "-30px auto 40px",
    padding: "8px 10px",
    background: "rgba(255,255,255,0.86)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    border: "1px solid rgba(76,0,76,0.1)",
    borderRadius: 40,
    boxShadow: "0 18px 44px rgba(26,0,26,0.16)",
    animation: "$heroFadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both",
  },
  filterPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#4c004c",
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: 30,
    padding: "8px 16px",
    transition: "all 0.28s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": {
      background: "rgba(76,0,76,0.06)",
    },
  },
  filterCount: {
    fontSize: "0.66rem",
    fontWeight: 700,
    minWidth: 19,
    height: 19,
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 6px",
    background: "rgba(76,0,76,0.08)",
    color: "#9b3b9b",
    transition: "all 0.28s ease",
  },
  filterPillActive: {
    color: "#ffffff",
    background: "linear-gradient(135deg, #6b1b6b 0%, #4c004c 100%)",
    boxShadow: "0 8px 20px rgba(76,0,76,0.35)",
    "&:hover": { background: "linear-gradient(135deg, #6b1b6b 0%, #4c004c 100%)" },
    "& $filterCount": {
      background: "rgba(255,255,255,0.22)",
      color: "#ffffff",
    },
  },
  emptyNote: {
    textAlign: "center",
    color: "#888888",
    fontSize: "0.9rem",
    padding: "40px 0",
  },
  groupBlockAll: {
    marginBottom: 40,
  },
  groupHead: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: 20,
  },
  groupLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "#4c004c",
    textTransform: "uppercase",
    letterSpacing: "2px",
    whiteSpace: "nowrap",
  },
  groupCount: {
    fontSize: "0.66rem",
    fontWeight: 700,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 6px",
    background: "rgba(76,0,76,0.08)",
    color: "#9b3b9b",
  },
  groupLine: {
    flex: 1,
    height: 1,
    background:
      "linear-gradient(90deg, rgba(76,0,76,0.18), rgba(76,0,76,0.02))",
  },

  // ── Awards ribbon ──
  awardsRibbon: {
    position: "relative",
    overflow: "hidden",
    marginTop: 48,
    borderRadius: 18,
    background:
      "linear-gradient(150deg, #3a003a 0%, #2d002d 55%, #1a001a 100%)",
    padding: "26px 28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "18px 40px",
    boxShadow: "0 16px 44px rgba(76,0,76,0.22)",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 260,
      height: 260,
      borderRadius: "50%",
      top: -130,
      right: -80,
      background:
        "radial-gradient(circle, rgba(242,193,78,0.16), transparent 70%)",
      pointerEvents: "none",
    },
  },
  awardsRibbonLabel: {
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "2.2px",
    textTransform: "uppercase",
    color: "#f2c14e",
    width: "100%",
    textAlign: "center",
    marginBottom: 2,
  },
  awardItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "11px",
    color: "#ffffff",
  },
  awardItemIcon: {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#3a2a00",
    background: "linear-gradient(135deg, #fbeeb5, #f2c14e)",
    boxShadow: "0 6px 16px rgba(242,193,78,0.35)",
    "& svg": { fontSize: 19 },
  },
  awardItemTitle: {
    fontSize: "0.8rem",
    fontWeight: 600,
    lineHeight: 1.25,
  },
  awardItemMeta: {
    fontSize: "0.66rem",
    color: "rgba(255,255,255,0.55)",
    marginTop: 1,
  },

  // ── Closing CTA band ──
  ctaBand: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 18,
    background: "linear-gradient(135deg, #6b1b6b 0%, #4a0e4a 100%)",
    padding: "38px 30px",
    textAlign: "center",
    boxShadow: "0 16px 44px rgba(76,0,76,0.25)",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 320,
      height: 320,
      borderRadius: "50%",
      top: -160,
      left: -100,
      background:
        "radial-gradient(circle, rgba(199,125,199,0.3), transparent 70%)",
      pointerEvents: "none",
    },
  },
  ctaBandTitle: {
    position: "relative",
    zIndex: 1,
    fontSize: "1.25rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 8,
    letterSpacing: "-0.01em",
  },
  ctaBandSub: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.82rem",
    color: "rgba(255,255,255,0.72)",
    maxWidth: 440,
    margin: "0 auto 20px",
    lineHeight: 1.6,
  },
  ctaBandBtn: {
    position: "relative",
    zIndex: 1,
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "#ffffff",
    color: "#4c004c",
    fontSize: "0.8rem",
    fontWeight: 600,
    borderRadius: 11,
    padding: "10px 24px",
    textDecoration: "none",
    boxShadow: "0 8px 22px rgba(0,0,0,0.2)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "&, &:hover, &:focus, &:active, &:visited": {
      color: "#4c004c",
      textDecoration: "none",
    },
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 14px 30px rgba(0,0,0,0.3)",
    },
    "& svg": {
      fontSize: 16,
      transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    "&:hover svg": { transform: "translateX(4px)" },
  },

  // ── Product card ──
  cardLink: {
    position: "relative",
    display: "block",
    height: "100%",
    textDecoration: "none",
    "&, &:hover, &:focus, &:active, &:visited": {
      textDecoration: "none",
    },
    "&:hover $cardGlow": {
      opacity: 0.55,
    },
  },
  cardGlow: {
    position: "absolute",
    inset: -3,
    borderRadius: 26,
    filter: "blur(22px)",
    opacity: 0,
    zIndex: 0,
    transition: "opacity 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    zIndex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "#ffffff",
    borderRadius: 22,
    overflow: "hidden",
    border: "1px solid rgba(76,0,76,0.07)",
    boxShadow: "0 6px 24px rgba(0,0,0,0.05)",
    transformStyle: "preserve-3d",
    transition:
      "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.4s ease, border-color 0.4s ease",
    "&:hover": {
      boxShadow: "0 30px 60px rgba(76,0,76,0.22)",
      borderColor: "rgba(76,0,76,0.14)",
    },
    "&:hover $spotlight": { opacity: 1 },
    "&:hover $shine": { transform: "translateX(240%) skewX(-18deg)" },
    "&:hover $cardMarkTile": {
      transform: "translateZ(30px) scale(1.08) rotate(-4deg)",
    },
    "&:hover $cardArrowWrap": {
      background: "#ffffff",
      borderColor: "#ffffff",
    },
    "&:hover $cardArrow": {
      transform: "translateX(3px)",
      color: "#4c004c",
    },
    "&:hover $chip": {
      transform: "translateY(-2px)",
    },
  },
  spotlight: {
    position: "absolute",
    inset: 0,
    zIndex: 3,
    opacity: 0,
    transition: "opacity 0.4s ease",
    pointerEvents: "none",
    background:
      "radial-gradient(260px circle at var(--mx, 50%) var(--my, 0%), rgba(199,125,199,0.16), transparent 60%)",
  },
  cardHeader: {
    position: "relative",
    padding: "22px 22px 20px",
    overflow: "hidden",
  },
  orb: {
    position: "absolute",
    borderRadius: "50%",
    pointerEvents: "none",
  },
  orbOne: {
    width: 150,
    height: 150,
    top: -70,
    right: -40,
    background:
      "radial-gradient(circle, rgba(255,255,255,0.22), transparent 68%)",
    animation: "$orbDrift 7s ease-in-out infinite",
  },
  orbTwo: {
    width: 120,
    height: 120,
    bottom: -55,
    left: -30,
    background:
      "radial-gradient(circle, rgba(255,255,255,0.14), transparent 68%)",
    animation: "$orbDriftAlt 9s ease-in-out infinite",
  },
  shine: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "45%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
    transform: "translateX(-220%) skewX(-18deg)",
    transition: "transform 0.85s cubic-bezier(0.22, 1, 0.36, 1)",
    pointerEvents: "none",
    zIndex: 2,
  },
  cardHeaderTop: {
    position: "relative",
    zIndex: 3,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  cardMarkTile: {
    width: 50,
    height: 50,
    borderRadius: 13,
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.28)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    boxShadow: "0 10px 26px rgba(0,0,0,0.22)",
    transition: "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
    "& svg": { fontSize: 26 },
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "0.6rem",
    fontWeight: 700,
    letterSpacing: "1px",
    textTransform: "uppercase",
    padding: "5px 11px",
    borderRadius: 20,
    color: "#ffffff",
    background: "rgba(255,255,255,0.16)",
    border: "1px solid rgba(255,255,255,0.3)",
    backdropFilter: "blur(4px)",
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#7CFC9B",
    animation: "$dotPulse 2.2s ease-in-out infinite",
  },
  statusDotSoon: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#f2c14e",
    animation: "$dotPulseSoon 2.2s ease-in-out infinite",
  },
  cardName: {
    position: "relative",
    zIndex: 3,
    color: "#ffffff",
    fontSize: "1.2rem",
    fontWeight: 700,
    marginTop: 14,
    letterSpacing: "-0.01em",
  },
  cardExpansion: {
    position: "relative",
    zIndex: 3,
    color: "rgba(255,255,255,0.85)",
    fontSize: "0.74rem",
    fontWeight: 400,
    marginTop: 3,
    lineHeight: 1.4,
  },

  cardBody: {
    position: "relative",
    zIndex: 3,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "18px 22px 20px",
  },
  categoryPill: {
    alignSelf: "flex-start",
    fontSize: "0.6rem",
    fontWeight: 700,
    color: "#9b3b9b",
    textTransform: "uppercase",
    letterSpacing: "1.4px",
    background:
      "linear-gradient(135deg, rgba(199,125,199,0.15), rgba(76,0,76,0.05))",
    border: "1px solid rgba(76,0,76,0.1)",
    borderRadius: 20,
    padding: "5px 12px",
    marginBottom: 14,
  },
  cardSummary: {
    fontSize: "0.8rem",
    color: "#555",
    lineHeight: 1.62,
    marginBottom: 16,
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "7px",
    marginBottom: 18,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "0.7rem",
    fontWeight: 500,
    color: "#4c004c",
    background: "#f6eef6",
    borderRadius: 9,
    padding: "6px 10px",
    transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease",
    "& svg": { fontSize: 14, color: "#9b3b9b" },
  },
  cardFooter: {
    marginTop: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },
  footerText: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#4c004c",
  },
  cardArrowWrap: {
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(76,0,76,0.2)",
    background: "transparent",
    transition: "all 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
  },
  cardArrow: {
    fontSize: 18,
    color: "#4c004c",
    transition: "all 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
  },

  // ── Founder quote ──
  quoteWrap: {
    background: "#ffffff",
    padding: "12px 0 64px",
  },
  quoteSection: {
    position: "relative",
    background: "linear-gradient(135deg, #6b1b6b 0%, #4a0e4a 100%)",
    borderRadius: 16,
    padding: "36px 28px",
    textAlign: "center",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 280,
      height: 280,
      borderRadius: "50%",
      top: -140,
      right: -100,
      background:
        "radial-gradient(circle, rgba(199,125,199,0.28), transparent 70%)",
      pointerEvents: "none",
    },
  },
  quoteText: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.95rem",
    color: "#ffffff",
    lineHeight: 1.7,
    fontStyle: "italic",
    maxWidth: 720,
    margin: "0 auto 14px",
  },
  quoteAuthor: {
    position: "relative",
    zIndex: 1,
    color: "#ffffff",
    fontSize: "0.8rem",
    fontWeight: 600,
    opacity: 0.9,
  },
}));

// Individual card, extracted so each one owns its cursor-follow tilt + spotlight.
const ProductCard = ({ product, index, classes }) => {
  const Icon = resolveIcon(product.icon);
  const tiltRef = useRef(null);

  const available =
    product.status === "live" || product.status === "saas";
  const statusText =
    product.status === "live"
      ? "Live"
      : product.status === "saas"
      ? "Available"
      : "Coming soon";

  const accentGradient = `linear-gradient(135deg, ${product.accent.from} 0%, ${product.accent.to} 100%)`;

  const handleMove = (e) => {
    const el = tiltRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    el.style.setProperty("--mx", `${x * 100}%`);
    el.style.setProperty("--my", `${y * 100}%`);
    const rotY = (x - 0.5) * 8;
    const rotX = (0.5 - y) * 8;
    el.style.transform = `perspective(1100px) rotateX(${rotX}deg) rotateY(${rotY}deg) translateY(-6px) scale(1.015)`;
  };

  const handleLeave = () => {
    const el = tiltRef.current;
    if (el) el.style.transform = "";
  };

  return (
    <Grow in={true} timeout={700 + index * 140}>
      <Link to={`/product/${product.slug}`} className={classes.cardLink}>
        <Box className={classes.cardGlow} style={{ background: accentGradient }} />
        <Box
          ref={tiltRef}
          className={classes.card}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
        >
          <Box className={classes.spotlight} />

          {/* Header */}
          <Box className={classes.cardHeader} style={{ background: accentGradient }}>
            <Box className={`${classes.orb} ${classes.orbOne}`} />
            <Box className={`${classes.orb} ${classes.orbTwo}`} />
            <Box className={classes.shine} />
            <Box className={classes.cardHeaderTop}>
              <Box
                className={classes.cardMarkTile}
                style={product.logo ? { background: "#ffffff", padding: 4 } : undefined}
              >
                {product.logo ? (
                  <img
                    src={product.logo}
                    alt={product.name}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <Icon />
                )}
              </Box>
              <Box className={classes.statusPill}>
                <span
                  className={available ? classes.statusDot : classes.statusDotSoon}
                />
                {statusText}
              </Box>
            </Box>
            <Typography className={classes.cardName}>{product.name}</Typography>
            <Typography className={classes.cardExpansion}>
              {product.expansion}
            </Typography>
          </Box>

          {/* Body */}
          <Box className={classes.cardBody}>
            <Typography className={classes.categoryPill}>
              {product.category}
            </Typography>
            <Typography className={classes.cardSummary}>
              {product.listSummary}
            </Typography>
            <Box className={classes.chipRow}>
              {product.highlights.map((h, i) => {
                const HIcon = resolveIcon(h.icon);
                return (
                  <span className={classes.chip} key={i}>
                    <HIcon />
                    {h.label}
                  </span>
                );
              })}
            </Box>
            <Box className={classes.cardFooter}>
              <span className={classes.footerText}>
                View {product.name}
              </span>
              <Box className={classes.cardArrowWrap}>
                <ArrowForwardIcon className={classes.cardArrow} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Link>
    </Grow>
  );
};

export const Products = () => {
  const classes = useStyles();

  const [metaTitle, setMetaTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [isloading, setIsloading] = useState(true);

  const handleClick = () => {
    const anchor = document.querySelector("#back-to-top-anchor");
    if (anchor) {
      anchor.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  useEffect(() => {
    getMetaData();
    getProducts();
  }, []);

  const getProducts = () => {
    Axios.get("/api/products")
      .then((res) => {
        if (res.data && res.data.status) {
          setProducts(res.data.data || []);
        }
      })
      .catch(() => {});
  };

  const getMetaData = () => {
    const formData = new FormData();
    formData.append("page", 3);
    Axios.post("/api/getmetadata", formData, {
      headers: { headKey: headKey },
    })
      .then((res) => {
        if (res.data.status) {
          setMetaTitle(res.data.data.meta_title);
          setMetaKeywords(res.data.data.meta_keywords);
          setMetaDesc(res.data.data.meta_desc);
        }
      })
      .finally(() => {
        setIsloading(false);
        handleClick();
      });
  };

  return (
    <div>
      <Helmet>
        <title>{metaTitle || "Our Products | Arawinz Soft Solutions"}</title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={metaKeywords}></meta>
      </Helmet>

      {/* Hero Section */}
      <Box className={classes.heroSection}>
        <HeroParticles id="products-hero-particles" />
        <Box className={`${classes.heroOrb} ${classes.heroOrbOne}`} />
        <Box className={`${classes.heroOrb} ${classes.heroOrbTwo}`} />
        <Box className={classes.heroContent}>
          <span className={classes.heroEyebrow}>The Arawinz Suite</span>
          <Typography className={classes.heroTitle}>
            Products built for real work
          </Typography>
          <Typography className={classes.heroSubtitle}>
            Apps, SaaS tools and games from one team. Pick anything below to
            see what it does and how to get it for your team.
          </Typography>
          <Box className={classes.heroStats}>
            <Box className={classes.heroStat}>
              <Typography className={classes.heroStatValue}>
                {products.length > 0 ? products.length : "5"}
                <span>+</span>
              </Typography>
              <Typography className={classes.heroStatLabel}>
                Products
              </Typography>
            </Box>
            <Box className={classes.heroStat}>
              <Typography className={classes.heroStatValue}>
                3<span>+</span>
              </Typography>
              <Typography className={classes.heroStatLabel}>
                Awards Won
              </Typography>
            </Box>
            <Box className={classes.heroStat}>
              <Typography className={classes.heroStatValue}>
                3
              </Typography>
              <Typography className={classes.heroStatLabel}>
                Platforms
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {isloading ? (
        <Loading />
      ) : (
        <>
          <Box className={classes.section}>
            {/* Floating category filter bar (overlaps the hero edge) */}
            <Box className={classes.filterBar}>
              {FILTERS.map((f) => {
                const count =
                  f.key === "all"
                    ? products.length
                    : products.filter((p) => groupOf(p) === f.key).length;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    className={`${classes.filterPill} ${
                      filter === f.key ? classes.filterPillActive : ""
                    }`}
                  >
                    {f.label}
                    <span className={classes.filterCount}>{count}</span>
                  </button>
                );
              })}
            </Box>

            <Container maxWidth="lg">
              {(() => {
                const renderGrid = (items) => (
                  <Grid container spacing={4} alignItems="stretch">
                    {items.map((product, index) => (
                      <Grid item xs={12} sm={6} md={4} key={product.slug}>
                        <ProductCard
                          product={product}
                          index={index}
                          classes={classes}
                        />
                      </Grid>
                    ))}
                  </Grid>
                );
                // A specific filter: one flat grid.
                if (filter !== "all") {
                  const shown = products.filter((p) => groupOf(p) === filter);
                  if (shown.length === 0) {
                    return (
                      <Typography className={classes.emptyNote}>
                        Nothing here yet. Coming soon.
                      </Typography>
                    );
                  }
                  return <div key={filter}>{renderGrid(shown)}</div>;
                }
                // "All": organised into labelled category groups.
                const groups = [
                  { key: "product", title: "Flagship Products" },
                  { key: "saas", title: "SaaS for Your Team" },
                  { key: "game", title: "Games" },
                ];
                return (
                  <div key="all">
                    {groups.map((g) => {
                      const items = products.filter(
                        (p) => groupOf(p) === g.key
                      );
                      if (items.length === 0) return null;
                      return (
                        <Box key={g.key} className={classes.groupBlockAll}>
                          <Box className={classes.groupHead}>
                            <span className={classes.groupLabel}>
                              {g.title}
                            </span>
                            <span className={classes.groupCount}>
                              {items.length}
                            </span>
                            <span className={classes.groupLine} />
                          </Box>
                          {renderGrid(items)}
                        </Box>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Awards ribbon (pulled from product data) */}
              {(() => {
                const awarded = products.find(
                  (p) => p.awards && p.awards.items && p.awards.items.length
                );
                if (!awarded) return null;
                return (
                  <Fade in={true} timeout={1100}>
                    <Box className={classes.awardsRibbon}>
                      <Typography className={classes.awardsRibbonLabel}>
                        Recognised across the industry
                      </Typography>
                      {awarded.awards.items.map((a, i) => {
                        const AIcon = resolveIcon(a.icon);
                        return (
                          <Box className={classes.awardItem} key={i}>
                            <Box className={classes.awardItemIcon}>
                              <AIcon />
                            </Box>
                            <Box>
                              <Typography className={classes.awardItemTitle}>
                                {a.title}
                              </Typography>
                              <Typography className={classes.awardItemMeta}>
                                {a.year} &bull; {a.place}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Fade>
                );
              })()}
            </Container>
          </Box>

          {/* Founder quote */}
          <Box className={classes.quoteWrap}>
            <Container maxWidth="lg">
              <Fade in={true} timeout={1200}>
                <Box className={classes.quoteSection}>
                  <TypingQuote
                    text={`"At Arawinz, we try to build what an industry actually needs. Every product here started with a real problem someone had."`}
                    className={classes.quoteText}
                  />
                  <Typography className={classes.quoteAuthor}>
                    - Chethan. P. S. S, Managing Director
                  </Typography>
                </Box>
              </Fade>

              {/* Closing CTA */}
              <Fade in={true} timeout={1300}>
                <Box className={classes.ctaBand} style={{ marginTop: 24 }}>
                  <Typography className={classes.ctaBandTitle}>
                    Have an idea worth building?
                  </Typography>
                  <Typography className={classes.ctaBandSub}>
                    Every product here started as a conversation. Tell us what
                    your business or industry needs, and we will build it with
                    you.
                  </Typography>
                  <Link to="/get-in-touch" className={classes.ctaBandBtn}>
                    Let's talk <ArrowForwardIcon />
                  </Link>
                </Box>
              </Fade>
            </Container>
          </Box>
        </>
      )}
    </div>
  );
};
